/**
 * Pending Calculations Modal Component
 *
 * Displays a warning when user attempts to save without recalculating pending changes.
 * Offers three options:
 * 1. Cancel - Go back and don't save
 * 2. Calculate - Opens calculation dialog to review and apply changes
 * 3. Save Anyway - Proceeds with save despite pending calculations
 *
 * Following CLAUDE.md standards:
 * - No defensive programming or fallbacks
 * - Direct field access only
 * - Type assertions where needed
 *
 * @component
 */

import React from 'react';
import { Modal, Button, Icon, Header, List } from 'semantic-ui-react';
import './Buttons.css';

interface PendingCalculationsModalProps {
  /** Whether the modal is open */
  open: boolean;

  /** Number of pending calculation changes */
  changesCount: number;

  /** Callback when user clicks Cancel */
  onCancel: () => void;

  /** Callback when user clicks Calculate */
  onCalculate: () => void;

  /** Callback when user clicks Save Anyway */
  onSaveAnyway: () => void;
}

/**
 * Modal warning user about pending calculations before saving
 *
 * @param open - Whether modal is visible
 * @param changesCount - Number of pending calculation changes
 * @param onCancel - Handler for Cancel button
 * @param onCalculate - Handler for Calculate button
 * @param onSaveAnyway - Handler for Save Anyway button
 * @returns Modal component
 */
const PendingCalculationsModal: React.FC<PendingCalculationsModalProps> = ({
  open,
  changesCount,
  onCancel,
  onCalculate,
  onSaveAnyway
}) => {
  return (
    <Modal
      className="budget-modal"
      open={open}
      size="small"
      onClose={onCancel}
      data-testid="pending-calculations-modal"
    >
      <Modal.Header>
        <Icon name="warning circle" color="orange" />
        Pending Calculations
      </Modal.Header>
      <Modal.Content>
        <Header as="h4">
          You have {changesCount} position{changesCount !== 1 ? 's' : ''} with pending quantity calculations
        </Header>
        <p>
          Changes have been made that affect position quantities, but calculations
          have not been applied yet.
        </p>
        <p style={{ marginTop: '1em' }}>
          <strong>What would you like to do?</strong>
        </p>
        <List bulleted style={{ marginTop: '0.5em' }}>
          <List.Item>
            <strong>Calculate:</strong> Review and apply the quantity changes before saving
          </List.Item>
          <List.Item>
            <strong>Save Anyway:</strong> Save current state without applying calculations
          </List.Item>
          <List.Item>
            <strong>Cancel:</strong> Go back without saving
          </List.Item>
        </List>
      </Modal.Content>
      <Modal.Actions>
        <Button
          onClick={onCancel}
          data-testid="pending-calc-cancel-button"
        >
          Cancel
        </Button>
        <Button
          primary
          onClick={onCalculate}
          data-testid="pending-calc-calculate-button"
        >
          <Icon name="calculator" />
          Calculate
        </Button>
        <Button
          color="orange"
          onClick={onSaveAnyway}
          data-testid="pending-calc-save-anyway-button"
        >
          <Icon name="save" />
          Save Anyway
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default PendingCalculationsModal;
